import React, { cloneElement, createRef, memo } from 'react'
import PropTypes from 'prop-types'
import { useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { DataGrid as DataGridMUI } from '@mui/x-data-grid'

import { Logger, LoggerTypes } from '../../../utils'
import { DataGridSearchToolbar } from './DataGridSearchToolbar'
import { DataGridNoRowsOverlay } from './DataGridNoRowsOverlay'
import { DataGridLoadingOverlay } from './DataGridLoadingOverlay'
import './style.scss'

/**
 * Generic DataGrid component.
 *
 * @link https://mui.com/components/data-grid/getting-started/
 * @link https://mui.com/api/data-grid/data-grid/
 * @see {DataGridSearchToolbar}
 *
 * @param {Object} props Ex: {
 *                            actions: [<ButtonIcon {...}/>, <ButtonIcon {...}/>],
 *                            columns: [{}],
 *                            rows: [],
 *                            searchText: '',
 *                            onSearch: (text) => {}
 *                        }
 * @returns {DataGridMUI}
 */
const DataGrid = ({
  actionsButtons,
  isLoading,
  columns,
  rows,
  searchValue,
  onSearch,
  checkboxSelection,
  hideFooterPagination,
  disableColumnFilter,
  exportData,
  onSelectionModelChange,
  onCellEditCommit,
  selectionModel
}) => {
  const { t } = useTranslation()
  const ref = createRef()
  const params = useParams()

  if (
    !isLoading &&
    actionsButtons &&
    actionsButtons.length > 0 &&
    !columns.some((el) => Boolean(el.field === 'actions'))
  ) {
    columns.push({
      field: 'actions',
      type: 'actions',
      headerName: t('common.actions'),
      width: 100,
      getActions: (el) =>
        actionsButtons.map((ButtonElement, index) =>
          cloneElement(ButtonElement, {
            key: `button-datagrid-action-${index}`,
            element: el.row,
            params: params
          })
        )
    })
  }

  let exportComponents = null

  if (exportData) {
    exportComponents = {
      Toolbar: DataGridSearchToolbar,
      LoadingOverlay: DataGridLoadingOverlay,
      NoRowsOverlay: DataGridNoRowsOverlay
    }
  }

  const _onSelectionModelChange = (rowIds) => {
    let currentSelectedRowId = null
    selectionModel.forEach((id) => {
      if (!rowIds.indexOf(id)) {
        currentSelectedRowId = id
      }
    })

    Logger.trace(LoggerTypes.CONTROLLER, 'DataGrid._onSelectionModelChange()', {
      selectionModel,
      rows,
      rowIds,
      currentSelectedRowId
    })

    return onSelectionModelChange(rowIds, currentSelectedRowId)
  }

  // if (!isLoading) {
  //   Logger.trace(LoggerTypes.CONTAINER, 'DataGrid.render()', { columns, rows, searchValue, selectionModel })
  // }

  return (
    <div style={{ display: 'flex', height: '100%' }}>
      <DataGridMUI
        ref={ref}
        style={{ display: 'flex', height: '400px' }}
        columnBuffer={2}
        columnThreshold={2}
        loading={isLoading}
        rows={rows}
        columns={columns}
        ColumnMenuIcon={<div />}
        editMode={'cell'} // Controls whether to use the 'cell' or 'row' editing.
        // rowsPerPageOptions={[10, 25, 50, 100]}
        rowsPerPageOptions={[100]}
        pageSize={100}
        // paginationMode={'client'} // 'client' | 'server'
        filterMode={'client'} // 'client' | 'server'
        density={'compact'} // 'comfortable' | 'compact' | 'standard'
        checkboxSelection={checkboxSelection}
        onSelectionModelChange={_onSelectionModelChange}
        onCellEditCommit={onCellEditCommit}
        selectionModel={selectionModel}
        disableSelectionOnClick={true}
        disableColumnFilter={disableColumnFilter}
        disableDensitySelector={true}
        disableColumnMenu={true}
        disableColumnSelector={false}
        disableVirtualization={false}
        autoPageSize={true}
        hideFooterPagination={hideFooterPagination}
        components={exportComponents}
        componentsProps={{
          toolbar: {
            searchValue: searchValue ? searchValue : '',
            onSearch: (event) => onSearch(event.target.value),
            onClearSearch: () => onSearch('')
          }
        }}
      />
    </div>
  )
}

DataGrid.displayName = 'DataGrid'

DataGrid.propTypes = {
  actionsButtons: PropTypes.array.isRequired,
  columns: PropTypes.array.isRequired,
  selectionModel: PropTypes.array,
  rows: PropTypes.any.isRequired,
  searchValue: PropTypes.string.isRequired,
  onSearch: PropTypes.func.isRequired,
  onSelectionModelChange: PropTypes.func,
  onCellEditCommit: PropTypes.func,
  isLoading: PropTypes.bool,
  hideFooterPagination: PropTypes.bool,
  disableColumnFilter: PropTypes.bool,
  exportData: PropTypes.bool,
  checkboxSelection: PropTypes.bool
}

DataGrid.defaultProps = {
  actionsButtons: [],
  columns: [],
  selectionModel: [],
  rows: [],
  searchValue: '',
  onSearch: () => {},
  onSelectionModelChange: () => {},
  onCellEditCommit: () => {},
  hideFooterPagination: true,
  disableColumnFilter: false,
  exportData: true,
  checkboxSelection: false
}

export default memo(DataGrid)
